import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStableLiveQuery } from '../../../../lib/db/useStableLiveQuery'
import { useStableEntityArray, baseEntityIdentity } from '../../../../lib/db/useStableEntityArray'
import { AnimatePresence, motion } from 'framer-motion'
import { db } from '../../../../lib/db/indexeddb'
import { scheduleSync } from '../../../../lib/sync/sync-engine'
import type { Task } from '../../../../lib/db/models'
import type { TaskListHandle } from '../../components/TaskList'
import { TaskBoard } from '../../components/TaskBoard'
import { useProjectNavigation } from '../../hooks/useProjectNavigation'
import { useTaskOperations } from '../../hooks/useTaskOperations'
import { useOptimisticTaskOverrides } from '../../hooks/useOptimisticTaskOverrides'
import { useTaskCreation } from '../../hooks/useTaskCreation'
import { useTaskDragAndDrop } from '../../hooks/useTaskDragAndDrop'
import { useSectionDrag, type OptimisticSectionUpdates } from '../../hooks/useSectionDrag'
import { useSectionDragAnimation } from '../../hooks/useSectionDragAnimation'
import { useProjectKeyboardShortcuts } from '../../hooks/useProjectKeyboardShortcuts'
import { useDuplicateItems } from '../../hooks/useDuplicateItems'
import { useContextMenuBadgeUpdate } from '../../hooks/useContextMenuBadgeUpdate'
import type { OptimisticUpdates } from '../../hooks/useTaskDragAndDrop'
import { useDndComposition } from '../ProjectPage/hooks/useDndComposition'
import { ClarifyEntriesIcon } from '../../../../components/icons/ClarifyEntriesIcon'
import { QuickNotesFilledOpenIcon, QuickNotesEmptyOpenIcon } from '../../../../components/icons/QuickNotesIcon'
import { ShortcutHint } from '../../../../components/ui/ShortcutHint'
import '../../../../components/ui/Button/Button.scss'
import { useQuickNotesSegments } from './hooks/useQuickNotesSegments'
import { useQuickNotesCollapsedState } from './hooks/useQuickNotesCollapsedState'
import { useClarifyLayoutActive } from './hooks/useClarifyLayoutActive'
import { useLocation } from 'react-router-dom'
import { useQuickNotesClarify } from './hooks/useQuickNotesClarify'
import { useScopedNavState } from '../../hooks/useScopedNavState'
import { ClarifySession } from './ClarifyView'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import '../TaskViews.scss'
import '../ProjectPage/ProjectPage.scss'
import './QuickNotesPage.scss'

interface QuickNotesPageProps {
  lifeSpace: 'personal' | 'work' | 'home'
}

export const QuickNotesPage: React.FC<QuickNotesPageProps> = ({ lifeSpace }) => {
  const isActive = useIsActiveTab()
  const { t } = useTranslation()

  // ── Data: orphan tasks for the current lifespace ──
  // Kurz-notiert is now a virtual queue of orphan tasks
  // (projectId == null && lifeSpace == X). Previously this was a real
  // system project per lifespace — see PRs 1 + 2 of the Task.lifeSpace
  // migration. Sections are still virtual (useQuickNotesSegments groups
  // by age). Stabilize task refs so tab-switch re-subscriptions bail.
  const tasksFromDexie = useStableLiveQuery(
    () => db.getQuickNotesByLifeSpace(lifeSpace),
    [lifeSpace],
  )
  const tasks = useStableEntityArray(tasksFromDexie, baseEntityIdentity)
  const noSections = useMemo(() => [], [])

  // Paused tasks (ruhend) don't appear in Kurz notiert or in a Clarify
  // session — they're resolved via the Ruhend-Ansicht. Use this filtered
  // list wherever the "active Kurz-notiert" view is needed; the raw
  // `tasks` is still used for optimistic-override reconciliation below.
  const activeTasks = useMemo(
    () => tasks?.filter(t => t.pausedAt == null),
    [tasks],
  )

  // ── Clarify mode ("Einträge klären") ──
  const { clarifyMode, clarifyTasks, enterClarify, exitClarify, toggleClarify } =
    useQuickNotesClarify(lifeSpace, activeTasks)
  // Layout class has to stay applied through the AnimatePresence exit fade —
  // otherwise the inner scroll list re-flows mid-fade and visibly "fans out".
  const { layoutActive: clarifyLayoutActive, handleExitComplete: handleClarifyExitComplete } =
    useClarifyLayoutActive(clarifyMode)

  // Global "K" toggles clarify mode from list view; inside ClarifyView, K is
  // handled by its own keydown listener (commit + exit). Gated on `isActive`
  // so inactive-but-mounted tabs don't steal the shortcut.
  useEffect(() => {
    if (!isActive) return
    if (clarifyMode) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return
      }
      if (e.key === 'k' || e.key === 'K') {
        if (!activeTasks || activeTasks.length === 0) return
        e.preventDefault()
        enterClarify()
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isActive, clarifyMode, activeTasks, enterClarify])

  // ── Collapsed state for virtual sections (persisted per lifespace) ──
  const { isCollapsed, toggleCollapsed } = useQuickNotesCollapsedState(lifeSpace)

  // ── Optimistic per-task field overrides ──
  // See ProjectPage for full rationale — every mutation handler routes
  // through this so user actions paint in the next frame instead of
  // waiting on the IDB write + LiveQuery refire (~1–2s when sync push/
  // pull cycles compete for the main thread).
  // QuickNotes uses *virtual* sections only (no Dexie row), so no
  // section-override map is wired here — virtual section state lives in
  // local React state already (`useQuickNotesCollapsedState`).
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(tasks)

  // Apply overrides on top of the raw task list. Used for both the
  // visible segments AND every downstream consumer that derives state
  // from tasks (creation, drag, navigation) so they all see the same
  // optimistic snapshot until Dexie confirms.
  const effectiveTasks = useMemo(() => {
    if (!tasks) return tasks
    // Paused tasks (ruhend) leave the Kurz-notiert list via the Clarify
    // "Ruhen lassen"-Aktion (`pausedAt = now`) and appear in Ruhend instead.
    if (optimisticTaskOverrides.size === 0) return tasks.filter(t => t.pausedAt == null)
    return tasks.flatMap(t => {
      const override = optimisticTaskOverrides.get(t.id)
      const merged = override ? { ...t, ...override } : t
      if (merged.pausedAt != null) return []
      return [merged]
    })
  }, [tasks, optimisticTaskOverrides])

  // ── Virtual segments: "Letzte 7 Tage" / "Älter" ──
  const segments = useQuickNotesSegments(
    effectiveTasks,
    t('quickNotes.sectionRecent'),
    t('quickNotes.sectionOlder'),
    isCollapsed,
  )

  // ── Local UI state ──
  const [optimisticUpdates, setOptimisticUpdates] = useState<OptimisticUpdates>(null)
  const [optimisticSectionUpdates, setOptimisticSectionUpdates] = useState<OptimisticSectionUpdates>(null)
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(null)
  const [hoveringTasks, setHoveringTasks] = useState(false)
  const [lastInteractedSegmentIndex, setLastInteractedSegmentIndex] = useState(0)

  // Refs
  const pageRef = useRef<HTMLDivElement>(null)
  const taskListRef = useRef<HTMLDivElement>(null)
  const taskListHandleRef = useRef<TaskListHandle | null>(null)
  const segmentHandleRefs = useRef(new Map<string, { current: TaskListHandle | null }>())
  const navFocusFirstRef = useRef<(() => void) | null>(null)
  const navClearSelectionRef = useRef<(() => void) | null>(null)
  const navSelectTaskRef = useRef<((taskId: string) => void) | null>(null)
  const navSelectSectionRef = useRef<((sectionId: string) => void) | null>(null)
  const navFocusedItemRef = useRef<{ type: 'task' | 'section'; id: string } | null>(null)
  const activeSegmentRef = useRef(0)
  const isDragAnimatingRef = useRef(false)

  const getSegmentHandleRef = useCallback((key: string) => {
    const map = segmentHandleRefs.current
    if (!map.has(key)) map.set(key, { current: null })
    return map.get(key)!
  }, [])

  // ── Task operations ──
  // Wired with the optimistic-override sink. Pass `effectiveTasks`
  // (override-applied) so a rapid second click reads the *intended*
  // status — not the still-stale raw Dexie row.
  const { handleUpdateTask, handleToggleComplete, handleDeleteTask } = useTaskOperations({
    t, setOptimisticTaskOverride, tasks: effectiveTasks,
  })

  // ── Task creation ──
  const {
    creatingPosition, creatingAfterTaskId, creatingSegmentIdx, pendingInlineTarget, morphData,
    positionMorphKey, inlineMorphTitle, inlineMorphSelected, inlineMorphKey, creationKey,
    inlineResetKey, skipInlineEntrance, autoSelectId,
    startCreating, startCreatingAfter,
    handleCreateTask, handleCreateTaskAndContinue,
    handleCreateTaskAfter, handleCreateTaskAfterAndContinue,
    handleCancelCreate, handleMorphComplete, handleInlineMorphComplete,
    topCreateRef, bottomCreateRef,
  } = useTaskCreation({
    projectId: undefined, lifeSpace, segments, tasks, sections: noSections,
    activeSegmentRef, navClearSelectionRef, navSelectTaskRef, navSelectSectionRef, navFocusedItemRef,
    setLastInteractedSegmentIndex,
  })

  // ── Cmd/Ctrl+Enter in expanded mode: save edit + open creator right after task ──
  const handleUpdateTaskAndCreateBelow = useCallback(async (taskId: string, data: Parameters<typeof handleUpdateTask>[1]) => {
    await handleUpdateTask(taskId, data)
    startCreatingAfter(taskId)
  }, [handleUpdateTask, startCreatingAfter])

  // ── Navigation ──
  const navigation = useProjectNavigation({
    segments,
    onEscapeTop: () => pageRef.current?.focus(),
    onDelete: handleDeleteTask,
    onDeleteSection: () => { /* no-op: virtual sections can't be deleted */ },
    onToggleComplete: handleToggleComplete,
    onToggleCollapse: toggleCollapsed,
    onCreateBelow: startCreatingAfter,
    onCreateInSection: () => { /* no-op for virtual sections */ },
    isDragAnimatingRef,
    skipFocusCorrectionRef: { current: null },
    sectionsReadonly: true,
    isActive,
  })
  const { clearSelection: navClearSelection, selectTask: navSelectTask } = navigation
  navFocusFirstRef.current = navigation.focusFirst
  navClearSelectionRef.current = navClearSelection
  navSelectTaskRef.current = navSelectTask
  navSelectSectionRef.current = navigation.selectSection

  // Preserve selection + focus per tab (scoped by route pathname) so
  // switching tabs/life spaces and coming back restores the same selection.
  const { pathname } = useLocation()
  useScopedNavState(pathname, {
    focusedNavIndex: navigation.focusedNavIndex,
    selectedIds: navigation.selectedIds,
    // Use the with-anchor setter so shift-select ranges after restore
    // originate from the restored focus index, not wherever anchorRef
    // happens to be on a fresh mount.
    setFocusedNavIndex: navigation.setFocusedNavIndexWithAnchor,
    setSelectedIds: navigation.setSelectedIds,
    clearSelection: navClearSelection,
  })

  // After a successful restore, hand keyboard focus to the list wrapper
  // so Arrow keys work without an extra click. The hook's restore runs on
  // mount, but TaskBoard (which owns wrapperRef) is gated behind
  // projectLoading — so wrapperRef.current is null at that point. We wait
  // until flatNavList has items AND wrapperRef is mounted, then focus once.
  const mountFocusDoneRef = useRef(false)
  useEffect(() => {
    if (mountFocusDoneRef.current) return
    if (navigation.focusedNavIndex === null) return
    const wrapper = navigation.wrapperRef.current
    if (!wrapper || navigation.flatNavList.length === 0) return
    wrapper.focus({ preventScroll: true })
    mountFocusDoneRef.current = true
  }, [navigation.focusedNavIndex, navigation.flatNavList.length, navigation.wrapperRef])

  // Re-focus the wrapper whenever this page becomes active. `display: none`
  // on inactive TabStack slots drops focus to <body>, so without this the
  // user had to click the list once after every tab switch to get keyboard
  // nav back.
  useEffect(() => {
    if (!isActive) return
    const rafId = requestAnimationFrame(() => {
      const active = document.activeElement
      const typingTag = active?.tagName
      if (typingTag === 'INPUT' || typingTag === 'TEXTAREA') return
      navigation.wrapperRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafId)
  }, [isActive, navigation.wrapperRef])
  const focusedItem = navigation.focusedNavIndex !== null ? navigation.flatNavList[navigation.focusedNavIndex] : null
  navFocusedItemRef.current = focusedItem ? { type: focusedItem.type, id: focusedItem.id } : null

  // Derive active segment for AddTask positioning
  const activeSegmentIndex = useMemo(() => {
    if (navigation.focusedNavIndex !== null) {
      const item = navigation.flatNavList[navigation.focusedNavIndex]
      if (item) {
        const segIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === item.id))
        if (segIdx !== -1) return segIdx
      }
    }
    if (hoveredSegmentIndex !== null && hoveredSegmentIndex < segments.length) {
      return hoveredSegmentIndex
    }
    return Math.min(lastInteractedSegmentIndex, Math.max(0, segments.length - 1))
  }, [hoveredSegmentIndex, navigation.focusedNavIndex, navigation.flatNavList, segments, lastInteractedSegmentIndex])
  activeSegmentRef.current = activeSegmentIndex

  const showAddTask = (hoveredSegmentIndex !== null && hoveringTasks) || navigation.focusedNavIndex !== null

  // Track last interacted segment from keyboard navigation
  useEffect(() => {
    if (navigation.focusedNavIndex === null) return
    const item = navigation.flatNavList[navigation.focusedNavIndex]
    if (!item) return
    const segIdx = segments.findIndex(seg => seg.tasks.some(t => t.id === item.id))
    if (segIdx !== -1) setLastInteractedSegmentIndex(segIdx)
  }, [navigation.focusedNavIndex, navigation.flatNavList, segments])

  // ── Drag-and-drop ──
  // Cross-segment drag is blocked: virtual sections are computed from createdAt,
  // a task dragged from "Älter" to "Letzte 7 Tage" would snap back on the next render.
  const {
    activeDragId, draggedIds, dragCount, isCrossSegmentDrag, gapAfterSegmentIdx, gapHeight,
    crossSegGapAfterRef, crossSegGapAfter,
    sensors, handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
  } = useTaskDragAndDrop({
    segments, tasks, sections: noSections, taskListRef, navSelectTask, setOptimisticUpdates,
    allowCrossSegmentDrag: false,
  })

  // Section drag is a no-op (no real sections); wire the hook anyway so TaskBoard props line up.
  const {
    activeSectionDragId,
    hoverTargetIdx,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
    sourceHeaderTopRef,
    handleSectionDragStart,
    handleSectionDragOver,
    handleSectionDragEnd,
    handleSectionDragCancel,
  } = useSectionDrag({
    segments, tasks, sections: noSections,
    wrapperRef: taskListRef,
    setOptimisticUpdates, setOptimisticSectionUpdates,
  })
  void hoverTargetIdx
  void segmentRectsRef
  void sourceHeaderHeightRef
  void optimisticSectionUpdates

  useSectionDragAnimation({
    wrapperRef: taskListRef,
    activeSectionDragId,
    hoverTargetIdx,
    segments,
    dropAnimRef: sectionDropAnimRef,
    segmentRectsRef,
    sourceHeaderHeightRef,
  })

  isDragAnimatingRef.current = activeDragId !== null || activeSectionDragId !== null

  const [sectionDragYOffset, setSectionDragYOffset] = useState(0)
  useLayoutEffect(() => {
    if (!activeSectionDragId) {
      setSectionDragYOffset(0)
      return
    }
    const headerEl = document.querySelector<HTMLElement>(`.section-header[data-section-id="${activeSectionDragId}"]`)
    if (headerEl) {
      setSectionDragYOffset(headerEl.getBoundingClientRect().top - sourceHeaderTopRef.current)
    }
  }, [activeSectionDragId, sourceHeaderTopRef])

  const excludeId = morphData || inlineMorphTitle ? autoSelectId ?? undefined : undefined
  const {
    composedDragStart, composedDragMove, composedDragOver, composedDragEnd, composedDragCancel,
    customCollisionDetection, allSortableIds, segmentAwareStrategy,
    sectionDragWidth, dragRotation, dragSourceSegIdx,
  } = useDndComposition({
    segments, excludeId,
    handleSectionDragStart, handleSectionDragOver, handleSectionDragEnd, handleSectionDragCancel,
    activeSectionDragId,
    handleDragStart, handleDragMove, handleDragOver, handleDragEnd, handleDragCancel,
    crossSegGapAfterRef, crossSegGapAfter,
  })

  const handleHoverLeave = useCallback(() => {
    if (document.querySelector('.add-task--open')) return
    setHoveredSegmentIndex(null)
    setHoveringTasks(false)
  }, [])

  // ── Duplicate items ──
  // Pass `effectiveTasks` (override-merged) so that a duplicate fired
  // immediately after a status-toggle clones the toggled state — not the
  // stale Dexie row that's still mid-LiveQuery-refire.
  const { handleDuplicate } = useDuplicateItems({
    projectId: undefined, segments, tasks: effectiveTasks, sections: noSections,
    selectedIds: navigation.selectedIds,
    flatNavList: navigation.flatNavList,
    focusedNavIndex: navigation.focusedNavIndex,
    setSelectedIds: navigation.setSelectedIds,
    setFocusedNavIndexWithAnchor: navigation.setFocusedNavIndexWithAnchor,
  })

  const handleDuplicateFromContextMenu = useCallback((taskId: string) => {
    const selected = navigation.selectedIds
    if (selected.size > 1 && selected.has(taskId)) {
      handleDuplicate()
    } else {
      handleDuplicate([taskId])
    }
  }, [handleDuplicate, navigation.selectedIds])

  const handleContextMenuBadgeUpdate = useContextMenuBadgeUpdate({
    getSelection: () => navigation.selectedIds,
    setOptimisticTaskOverride,
    logTag: 'QuickNotesPage',
  })

  // ── Keyboard shortcuts ──
  useProjectKeyboardShortcuts({
    savedName: t('quickNotes.title'),
    tasks, creatingPosition, creatingAfterTaskId, segments,
    activeSegmentRef, navClearSelection, navSelectTask,
    startCreating, startCreatingAfter,
    handleAddSection: () => { /* no-op: no sections on Quick Notes */ },
    handleDuplicate,
    focusedNavIndex: navigation.focusedNavIndex,
    flatNavList: navigation.flatNavList,
    sectionsReadonly: true,
    isActive,
  })

  return (
    <div
      ref={pageRef}
      className={`task-view quick-notes-page project-page${clarifyLayoutActive ? ' quick-notes-page--clarify-mode' : ''}`}
    >
      <div className="quick-notes-page__header">
        <h1 className="quick-notes-page__title">
          {(tasks?.length ?? 0) > 0
            ? <QuickNotesFilledOpenIcon size={18} className="quick-notes-page__title-icon" />
            : <QuickNotesEmptyOpenIcon size={18} className="quick-notes-page__title-icon" />}
          {t('quickNotes.title')}
        </h1>
        <p className="quick-notes-page__subtitle">{t('quickNotes.subtitle')}</p>
      </div>

      <AnimatePresence mode="wait" initial={false} onExitComplete={handleClarifyExitComplete}>
        {clarifyMode && clarifyTasks ? (
          <motion.div
            key="clarify"
            className="quick-notes-page__content-group"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <ClarifySession
              tasks={clarifyTasks}
              lifeSpace={lifeSpace}
              onExit={exitClarify}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            className="quick-notes-page__content-group"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {tasks && tasks.length > 0 && (
              <div className="quick-notes-page__clarify-bar">
                <button
                  type="button"
                  className="button button--outline button--size-default quick-notes-page__clarify-button"
                  onClick={toggleClarify}
                >
                  <span className="button__icon"><ClarifyEntriesIcon /></span>
                  <span className="button__text">{t('quickNotes.clarifyEntries')}</span>
                  <span className="quick-notes-page__clarify-button-hint">
                    <ShortcutHint variant="emphasized">K</ShortcutHint>
                  </span>
                </button>
              </div>
            )}
            <div className="quick-notes-page__mode-slot">
              <TaskBoard
        segments={segments}
        tasks={tasks}
        projectId={undefined}
        savedName={t('quickNotes.title')}
        gapAfterSegmentIdx={gapAfterSegmentIdx}
        gapHeight={gapHeight}
        activeDragId={activeDragId}
        draggedIds={draggedIds}
        dragCount={dragCount ?? 0}
        isCrossSegmentDrag={isCrossSegmentDrag}
        activeSectionDragId={activeSectionDragId}
        dragSourceSegIdx={dragSourceSegIdx}
        sectionDragWidth={sectionDragWidth}
        dragRotation={dragRotation}
        sectionDragYOffset={sectionDragYOffset}
        taskListRef={taskListRef}
        taskListHandleRef={taskListHandleRef}
        getSegmentHandleRef={getSegmentHandleRef}
        sensors={sensors}
        customCollisionDetection={customCollisionDetection}
        allSortableIds={allSortableIds}
        segmentAwareStrategy={segmentAwareStrategy}
        composedDragStart={composedDragStart}
        composedDragMove={composedDragMove}
        composedDragOver={composedDragOver}
        composedDragEnd={composedDragEnd}
        composedDragCancel={composedDragCancel}
        navigation={navigation}
        hoveredSegmentIndex={hoveredSegmentIndex}
        setHoveredSegmentIndex={setHoveredSegmentIndex}
        hoveringTasks={hoveringTasks}
        setHoveringTasks={setHoveringTasks}
        activeSegmentIndex={activeSegmentIndex}
        showAddTask={showAddTask}
        creatingSegmentIdx={creatingSegmentIdx}
        creatingPosition={creatingPosition}
        creatingAfterTaskId={creatingAfterTaskId}
        pendingInlineTarget={pendingInlineTarget}
        creationKey={creationKey}
        inlineResetKey={inlineResetKey}
        skipInlineEntrance={skipInlineEntrance}
        autoSelectId={autoSelectId}
        morphData={morphData}
        positionMorphKey={positionMorphKey}
        inlineMorphTitle={inlineMorphTitle}
        inlineMorphSelected={inlineMorphSelected}
        inlineMorphKey={inlineMorphKey}
        topCreateRef={topCreateRef}
        bottomCreateRef={bottomCreateRef}
        startCreating={startCreating}
        handleCreateTask={handleCreateTask}
        handleCreateTaskAndContinue={handleCreateTaskAndContinue}
        handleCreateTaskAfter={handleCreateTaskAfter}
        handleCreateTaskAfterAndContinue={handleCreateTaskAfterAndContinue}
        handleCancelCreate={handleCancelCreate}
        handleMorphComplete={handleMorphComplete}
        handleInlineMorphComplete={handleInlineMorphComplete}
        autoEditSectionId={null}
        handleToggleCollapse={toggleCollapsed}
        handleRenameSection={() => { /* no-op: virtual sections */ }}
        handleDeleteSection={() => { /* no-op: virtual sections */ }}
        handleAddSection={() => { /* no-op: virtual sections */ }}
        handleToggleComplete={handleToggleComplete}
        handleDeleteTask={handleDeleteTask}
        handleUpdateTask={handleUpdateTask}
        handleUpdateTaskAndCreateBelow={handleUpdateTaskAndCreateBelow}
        handleDuplicate={handleDuplicateFromContextMenu}
        handleContextMenuBadgeUpdate={handleContextMenuBadgeUpdate}
        optimisticUpdates={optimisticUpdates}
        handleHoverLeave={handleHoverLeave}
        handleClearSelection={navClearSelection}
        sectionsReadonly={true}
        allowCrossSegmentDrag={false}
      />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
